/**
 * Import React libraries.
 */
import * as React from 'react';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';

/**
 * Import custom components.
 */
import {ChipInputContainer, ChipInputField} from './ChipInputField';
import {Size, States} from '../types';
import {DropdownContent, DropdownItem} from '../Dropdown';
import {SystemIcons} from '../icons';
import {ChipInputProps, ChipItem} from './ChipInputTypes';
import {useClickOutsideRef, useFocusOutsideRef} from '../common';
import {Z_INDEXES} from '../styles';

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;

  ${ChipInputContainer} {
    position: relative;

    &:focus, &.focus-visible-within {
      z-index: ${Z_INDEXES.dropdown + 1} !important;
    }
  }
`;


interface ListItem extends ChipItem {
  secondaryLabel?: string;
  value: string;
}

export interface ChipDropdownInputProps extends Omit<ChipInputProps, 'autoSave'> {
  /** Array of items available. */
  list: ListItem[] | string[];
  /** On input selected/unselected. */
  onInputChange?: (input: string) => void;
  /** Message to show in the dropdown if none of the chips fits user input. */
  messageOnNoResults?: string;
  /** Message to show in the dropdown if none of the chips fits user input. */
  suggestion?: ListItem;
  /** If flag is set then loader will be shown in the input field. */
  loading?: boolean;
  /** Max number of chips that user can select. */
  maxSelectedItems?: number;
  /** Disable sorting of options in the dropdown. */
  disableSorting?: boolean;
  /** Disable filtering of options depending on user input in the dropdown. */
  disableFiltering?: boolean;
}

export const ChipDropdownInput = React.forwardRef<HTMLInputElement | undefined, ChipDropdownInputProps>(({
                                                                                               list,
                                                                                               onInputChange,
                                                                                               disableSorting,
                                                                                               messageOnNoResults,
                                                                                               values,
                                                                                               inputId,
                                                                                               variants,
                                                                                               placeholder,
                                                                                               validationMessage,
                                                                                               onValueChange,
                                                                                               suggestion,
                                                                                               loading,
                                                                                               maxSelectedItems,
                                                                                               multiLine = true,
                                                                                               size = Size.Medium,
                                                                                               icon,
                                                                                               disableFiltering,
                                                                                               disabled,
                                                                                               altPlaceholder,
                                                                                               required,
                                                                                               ...rest
                                                                                             }: ChipDropdownInputProps, ref) => {
  // Initiate states
  const [givenList, setGivenList] = React.useState<ListItem[]>([]);
  const [chips, setChips] = React.useState<ListItem[]>([]);

  const [activeDescendant, setActiveDescendant] = React.useState<string>();
  const [value, setValue] = React.useState<string>('');
  const [restartFilter, setRestartFilter] = React.useState<boolean>(false);
  const [filteredList, setFilteredList] = React.useState<ListItem[]>();

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState<boolean>(false);
  const [dropdownList, setDropdownList] = React.useState<DropdownItem[]>([]);
  const [dropdownValue, setDropdownValue] = React.useState<string>('');

  const [focused, setFocused] = React.useState<number | null>(null);

  const containerRef = useClickOutsideRef(() => setDropdownIsOpen(false), [], useFocusOutsideRef(() => setDropdownIsOpen(false)));
  const inputRef = React.useRef<any>(undefined);

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);


  /**
   * Sets initial values for the chips.
   */
  React.useEffect(() => {

    const list =
      values.map((a) => chips.find(b => b.value === a) || givenList.find(b => b.value === a) || {
        label: a,
        value: a,
        icon: icon
      })
        .map((a, index) => ({...a, variant: a.variant || (variants && variants[index]) || States.Default}))
        .map((a, index) => ({...a, icon: a.icon || icon}));

    setChips(list);
  }, [values, variants, icon, givenList]);

  /**
   * Convert string[] to List[] if necessary
   */
  React.useEffect(() => {
    if (typeof list[0] === 'string') {
      const newList: ListItem[] = (list as string[]).map((e: string) => ({label: e, value: e}));
      setGivenList(newList);
    } else {
      setGivenList(list as ListItem[]);
    }
  }, [list]);

  /**
   * filter and sort list if remoteOperations is false
   * */
  React.useEffect(() => {
    let options = [...givenList];
    if (!disableFiltering) {
      if (!restartFilter && value !== '') {
        options = options.filter((option) => option.label.toUpperCase().indexOf(value.toUpperCase()) !== -1 ||
          (option.secondaryLabel && option.secondaryLabel?.toUpperCase().indexOf(value.toUpperCase()) !== -1));
      }
    }

    if (!disableSorting) {
      options = options.sort();
    }

    suggestion && options.push(suggestion);

    options = options.filter((o) => !chips.find(a => a.value === o.value));
    setFilteredList(options);
  }, [value, givenList, chips, disableFiltering, disableSorting]);

  /**
   * if there is only one active item in dropdown mark it as focused
   * */
  React.useEffect(() => {
    if (dropdownIsOpen && !!value && dropdownList?.filter(a => !a.disabled).length === 1) {
      setDropdownValue(dropdownList.find(a => !a.disabled)!.value);
    } else {
      setDropdownValue('');
    }
  }, [dropdownList, dropdownIsOpen, value]);

  const handleClickOutside = (e: any) => {
    if (!containerRef.current?.contains(e.target)) {
      if (dropdownIsOpen) {
        setDropdownIsOpen(false);
        if (!givenList.find((e) => e.value === value)) {
          setValue('');
        }
      }
    }
  };

  /**
   * register click outside event listener
   * */
  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  /**
   * update dropdown items when filtered list changes
   * */
  React.useEffect(() => {
    setDropdownList(filteredList?.map(x => ({
      value: x.value,
      displayLabel: x.label,
      noteLabel: x.secondaryLabel,
      icon: (x.icon ?? icon),
      disabled: x.disabled,
    })) ?? []);
  }, [filteredList]);

  const addChip = (newChip: ListItem) => {
    const newList = [...chips, newChip];
    // Let's add the chip
    setChips(newList);
    onValueChange(newList.map(a => a.value));

    setValue('');
  };

  const handleInputValueChange = (value: string) => {
    setValue(value);
    setRestartFilter(false);
    setDropdownIsOpen(true);
    onInputChange && onInputChange(value);
  }

  /**
   * Add chip from dropdown
   * */
  const handleValueUpdate = (values: string[]) => {
    const item = filteredList?.find(a => a.value === values[0]);
    item && addChip(item);
    setRestartFilter(true);
    inputRef?.current?.focus();
  }

  /**
   * Adds a chip to the chip list.
   * @param event - Event handler from the key down event.
   */
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    // Let's trigger only on enter, comma and space key press
    if ((event.key === 'Enter' || event.key === ' ') && value) {
      if (dropdownList?.filter(a => !a.disabled).length === 1) {
        handleValueUpdate([dropdownList!.find(a => !a.disabled)!.value]);
        event.stopPropagation();
      }
    }

    if ((event.key === 'ArrowDown' || event.key === 'Down' || event.key === 'ArrowUp' || event.key === 'Up') && !!dropdownList?.length) {
      setDropdownIsOpen(true);
    }
  };

  /**
   * Removes a specific chip from the chip list.
   * @param index - Index of the chip that needs to be removed.
   */
  const handleOnChipRemoved = (index: number): void => {
    // Let's copy the chips
    const newChips = [...chips];

    // Let's delete the chip
    // If necessary, let's replace it with a different chip
    newChips.splice(index, 1);

    // Let's update the chip list
    setChips([...newChips]);
    onValueChange(newChips.map((e) => e.value));
  };

  return (
    <Wrapper ref={containerRef}>
      <ChipInputField inputId={inputId}
                      ref={inputRef}
                      items={chips}
                      required={required}
                      inputValue={value}
                      role="combobox"
                      aria-activedescendant={activeDescendant}
                      size={size}
                      placeholder={placeholder}
                      altPlaceholder={altPlaceholder}
                      disabled={disabled}
                      multiLine={multiLine}
                      validationMessage={validationMessage}
                      loading={loading}
                      onRemoveItem={(chip, index) => handleOnChipRemoved(index)}
                      onInputValueChange={handleInputValueChange}
                      onInputKeyDown={handleInputKeyDown}
                      onActivationChange={(value) => setDropdownIsOpen(value)}
                      {...rest}/>
      <DropdownContent
        containerRef={containerRef}
        customizationProps={{
          action: () => {
          },
          pinTopItem: true,

          itemsType: 'normal',
          scrollable: true,
          actionLabel: '',
          multiSelect: false,
          onValueUpdate: handleValueUpdate,
          items: dropdownList,
        }}
        ariaRolesType="input"
        focused={focused}
        setFocused={setFocused}
        onActiveDescendantChanged={(e) => setActiveDescendant(e)}
        selectedValues={[dropdownValue]}
        messageOnNoResults={messageOnNoResults ?? ''}
        setSelectedValues={() => {
        }}
        isOpen={(!maxSelectedItems || (!!maxSelectedItems && chips.length < maxSelectedItems)) && dropdownIsOpen}
        setIsOpen={setDropdownIsOpen}
        filter={''}
        isButton={false}
        id={`${inputId}_dropdowncontent`}/>

    </Wrapper>
  );
});
